import { useTranslation } from 'react-i18next';
import { Check, ExternalLink } from 'lucide-react';

import {
  Box,
  Stack,
  Button,
  Popover,
  Tooltip,
  ButtonBase,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';

import { motion as motionTokens } from '@nexus/ari-lib/theme/motion';

import { WorkspaceAvatar } from './workspace-avatar';
import { useCurrentWorkspace, useWorkspaces, Workspace } from '../hooks/use-workspaces';
import { workspacePlanLabel } from '../lib/workspace-utils';

type WorkspaceSwitcherMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
  onSelect: (workspace: Workspace) => void;
  manageUrl: string;
};

export function WorkspaceSwitcherMenu({
  anchorEl,
  open,
  onClose,
  onRetry,
  onSelect,
  manageUrl,
}: WorkspaceSwitcherMenuProps) {
  const { t } = useTranslation();
  const { userWorkspaces: workspaces, isLoading, error } = useWorkspaces();
  const { currentWorkspaceId } = useCurrentWorkspace();

  const handleSelect = (workspace: Workspace) => {
    if (workspace.id === currentWorkspaceId) {
      onClose();
      return;
    }
    onSelect(workspace);
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            p: 0.5,
            borderRadius: 1.5,
            bgcolor: 'container.low',
            // Same fill as the card, so the panel needs the collapsed trigger's
            // stroke to have an edge at all.
            border: '1px solid',
            borderColor: 'grey.800',
            boxShadow: theme.customShadows.dropdown,
            mt: theme.spacing(1),
          }),
        },
      }}
    >
      <Box data-testid="cloud.workspace.menu">
        <Typography variant="overline" sx={{ display: 'block', px: 1.5, py: 1, color: 'text.secondary' }}>
          Workspaces
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error !== null ? (
          <Stack spacing={0.5} sx={{ alignItems: 'flex-start', px: 1.5, pb: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Workspaces did not load.
            </Typography>
            {onRetry && (
              <Button size="small" onClick={onRetry} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>
                Retry
              </Button>
            )}
          </Stack>
        ) : (
          <Stack spacing={0.25}>
            {workspaces.map((workspace) => (
              <WorkspaceMenuRow
                key={workspace.id}
                workspace={workspace}
                isCurrent={workspace.id === currentWorkspaceId}
                manageUrl={manageUrl}
                manageLabel={t('workspaces.manageWorkspace', { name: workspace.shortName })}
                onSelect={handleSelect}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Popover>
  );
}

type WorkspaceMenuRowProps = {
  workspace: Workspace;
  isCurrent: boolean;
  manageUrl: string;
  manageLabel: string;
  onSelect: (workspace: Workspace) => void;
};

function WorkspaceMenuRow({ workspace, isCurrent, manageUrl, manageLabel, onSelect }: WorkspaceMenuRowProps) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', px: 0.5 }}>
      <ButtonBase
        aria-current={isCurrent ? 'true' : undefined}
        onClick={() => onSelect(workspace)}
        sx={{
          flex: 1,
          minWidth: 0,
          gap: 1,
          px: 1,
          py: 1,
          borderRadius: 1,
          justifyContent: 'flex-start',
          '&:hover': { bgcolor: 'container.medium' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: -2 },
          transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}`,
        }}
      >
        <WorkspaceAvatar workspace={workspace} />

        <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
          <Tooltip title={workspace.shortName} placement="top">
            <Typography variant="subtitle2" noWrap sx={{ display: 'block', fontWeight: 700 }}>
              {workspace.shortName}
            </Typography>
          </Tooltip>
          <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
            {workspacePlanLabel(workspace.subscriptionPlan)}
          </Typography>
        </Box>

        {isCurrent && <Check size={16} aria-hidden />}
      </ButtonBase>

      {isCurrent && (
        <IconButton
          component="a"
          href={manageUrl}
          target="_blank"
          rel="noopener"
          aria-label={manageLabel}
          size="small"
          sx={{
            flexShrink: 0,
            color: 'grey.400',
            '&:hover': { color: 'grey.50', bgcolor: 'container.medium' },
          }}
        >
          <ExternalLink size={14} />
        </IconButton>
      )}
    </Stack>
  );
}
